<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 overflow-y-auto"
    @click.self="close"
  >
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- Background overlay -->
      <div
        class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
        @click="close"
      />

      <!-- Modal panel -->
      <div class="inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 rounded-lg shadow-xl">
        <!-- Header -->
        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between">
            <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
              {{ t('app.subscription.renewal.title') }}
            </h3>
            <button
              class="text-gray-400 hover:text-gray-500"
              @click="close"
            >
              <XMarkIcon class="w-6 h-6" />
            </button>
          </div>
          
          <!-- Progress Steps -->
          <RenewalProgressIndicator :current-step="currentStep" />
        </div>

        <!-- Content -->
        <div class="px-6 py-6">
          <!-- Step 1: Plan Selection -->
          <PlanSelectionStep
            v-if="currentStep === 1"
            :available-plans="availablePlans"
            :model-value="{ selectedPlanId, billingCycle }"
            @update:selected-plan-id="selectedPlanId = $event"
            @update:billing-cycle="billingCycle = $event"
          />

          <!-- Step 2: Payment Instructions -->
          <PaymentInstructionsStep
            v-if="currentStep === 2"
            :payment-data="paymentData"
            @copy-reference="copyReference"
          />

          <!-- Step 3: Upload Proof -->
          <UploadProofStep
            v-if="currentStep === 3"
            :model-value="{ paymentDate, proofImageUrl, notes }"
            @update:payment-date="paymentDate = $event"
            @update:proof-image-url="proofImageUrl = $event"
            @update:notes="notes = $event"
          />

          <!-- Step 4: Confirmation -->
          <ConfirmationStep v-if="currentStep === 4" />
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
          <div class="flex justify-between">
            <button 
              v-if="currentStep > 1 && currentStep < 4"
              class="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              @click="previousStep"
            >
              {{ t('app.common.back') }}
            </button>
            <div v-else />

            <div class="flex gap-2">
              <button 
                v-if="currentStep < 4"
                class="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                @click="close"
              >
                {{ t('app.common.cancel') }}
              </button>

              <button 
                v-if="currentStep === 1"
                :disabled="!selectedPlanId || loading"
                class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                @click="handleRequestRenewal"
              >
                {{ loading ? t('app.common.loading') : t('app.subscription.renewal.continue') }}
              </button>

              <button 
                v-if="currentStep === 2"
                class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                @click="nextStep"
              >
                {{ t('app.subscription.renewal.already_paid') }}
              </button>

              <button 
                v-if="currentStep === 3"
                :disabled="!paymentDate || loading"
                class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                @click="handleSubmitProof"
              >
                {{ loading ? t('app.common.loading') : t('app.subscription.renewal.submit_proof') }}
              </button>

              <button 
                v-if="currentStep === 4"
                class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                @click="close"
              >
                {{ t('app.subscription.renewal.close') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { useRenewalPayment } from '@/composables/useRenewalPayment'
import RenewalProgressIndicator from '@/components/subscription/RenewalProgressIndicator.vue'
import PlanSelectionStep from '@/components/subscription/PlanSelectionStep.vue'
import PaymentInstructionsStep from '@/components/subscription/PaymentInstructionsStep.vue'
import UploadProofStep from '@/components/subscription/UploadProofStep.vue'
import ConfirmationStep from '@/components/subscription/ConfirmationStep.vue'

const { t } = useI18n()

interface Props {
  isOpen: boolean
  availablePlans: any[]
  currentPlanId?: number | null
  currentBillingCycle?: 'monthly' | 'annual'
}

const props = defineProps<Props>()
const emit = defineEmits(['close', 'success'])

// Debug logging
console.log('RenewalPaymentModal - props:', props)
console.log('RenewalPaymentModal - availablePlans:', props.availablePlans)

const {
  currentStep,
  loading,
  selectedPlanId,
  billingCycle,
  paymentData,
  paymentDate,
  proofImageUrl,
  notes,
  resetForm,
  nextStep,
  previousStep,
  requestRenewal,
  copyReference,
  submitProof,
  watchModalOpen
} = useRenewalPayment(props.availablePlans)

const close = () => {
  emit('close')
  resetForm()
}

const handleRequestRenewal = async () => {
  if (!selectedPlanId.value) return
  
  try {
    await requestRenewal({
      plan_id: selectedPlanId.value,
      billing_cycle: billingCycle.value
    })
  } catch (error) {
    // Error handling is done in the composable
  }
}

const handleSubmitProof = async () => {
  if (!paymentData.value || !paymentDate.value) return
  
  try {
    await submitProof({
      reference_number: paymentData.value.reference_number,
      payment_date: paymentDate.value,
      proof_image_url: proofImageUrl.value || undefined,
      notes: notes.value || undefined
    })
    emit('success')
  } catch (error) {
    // Error handling is done in the composable
  }
}

// Watch for modal opening and pre-select current plan
watch(() => props.isOpen, (isOpen) => {
  watchModalOpen(isOpen, props.currentPlanId, props.currentBillingCycle)
})
</script>
